/* eslint-disable max-lines */
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Link, RadioButton, Switch, Tooltip } from 'src/components/atoms';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { CategoryType } from 'src/graphql';
import { useTheme } from 'styled-components';
import {
  customIntakeMethodUnavailableTooltip,
  flowBlockedByLinkedFormTooltip,
  goTogetherDescription,
  unavailableTooltip,
} from './categoryBodyDynamicQuestionsCopy';
import { effectiveCategory } from './categoryBodyDynamicQuestionsEffectiveCategory';
import {
  CustomFlowNotice,
  CustomFlowNoticeBody,
  CustomFlowNoticeHeader,
  CustomFlowNoticeTitle,
  DynamicLayoutRoot,
  IconTooltipTriggerButton,
  IntakeChannelLabel,
  IntakeChannelRow,
  IntakeChannelRowSubOption,
  QuestionFlowColumn,
  QuestionFlowContent,
  QuestionFlowOuter,
  RadioStack,
  SectionDescription,
  SectionHeading,
  TabButton,
  TabButtonLabelRow,
  TabButtonLabelText,
  TabButtonTrailing,
  TabRow,
  TopCategoryDescription,
  TopCategoryRow,
  TopCategorySwitchCell,
  TopCategoryTitle,
  ValidationMessage,
} from './CategoryBodyDynamicQuestionsLayout.styles';
import {
  type DefaultFlowIntakeSnapshot,
  createCategoryBodyHandleTabChange,
  isDefaultQuestionFlowIntakeValid,
  mergeCategoryWithDefaultFlowIntakeSnapshot,
  patchDefaultQuestionFlowVisibleChannels,
  readDefaultFlowIntakeSnapshot,
  setCategoryQuestion,
  TAB_CUSTOM,
  TAB_DEFAULT,
  toggleCategoryProperty,
  toggleIntakeMethod,
  toggleReportedCategoryOrQuestion,
} from './helpers';
import { useTooltipHoverOrFocusVisibility } from './useTooltipHoverOrFocusVisibility';

interface Props {
  category: CategoryType;
  currentCategory: CategoryType | null;
  setCurrentCategory: Dispatch<SetStateAction<CategoryType | null>>;
  isCurrentCategory: boolean;
  vaultTalkEnabled: boolean;
  slackEnabled: boolean;
  oprEnabled: boolean;
  goTogetherEnabled: boolean;
  dynamicQuestionsV1: boolean;
  isLegacyMode: boolean;
  onDefaultFlowIntakeValidityChange?: (valid: boolean) => void;
}

export const CategoryBodyDynamicQuestionsLayout = ({
  category,
  currentCategory,
  setCurrentCategory,
  isCurrentCategory,
  vaultTalkEnabled,
  slackEnabled,
  oprEnabled,
  goTogetherEnabled,
  dynamicQuestionsV1,
  isLegacyMode,
  onDefaultFlowIntakeValidityChange,
}: Props) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(TAB_DEFAULT);
  const [goTogetherTooltipRoot, setGoTogetherTooltipRoot] =
    useState<HTMLElement | null>(null);
  const goTogetherTooltipVisibility = useTooltipHoverOrFocusVisibility();
  const [
    customFlowUnavailableTooltipRoot,
    setCustomFlowUnavailableTooltipRoot,
  ] = useState<HTMLElement | null>(null);
  const vaultTalkTooltipVisibility = useTooltipHoverOrFocusVisibility();
  const [customFlowVaultTalkTooltipRoot, setCustomFlowVaultTalkTooltipRoot] =
    useState<HTMLElement | null>(null);
  const slackTooltipVisibility = useTooltipHoverOrFocusVisibility();
  const [customFlowSlackTooltipRoot, setCustomFlowSlackTooltipRoot] =
    useState<HTMLElement | null>(null);
  const customFlowUnavailableTooltipVisibility =
    useTooltipHoverOrFocusVisibility();
  const resetCustomFlowUnavailableTooltipRef = useRef(
    customFlowUnavailableTooltipVisibility.reset,
  );
  resetCustomFlowUnavailableTooltipRef.current =
    customFlowUnavailableTooltipVisibility.reset;
  const defaultFlowIntakeSnapshotsByCategoryIdRef = useRef(
    new Map<number, DefaultFlowIntakeSnapshot>(),
  );

  const cat = effectiveCategory(category, currentCategory, isCurrentCategory);
  const reported = Boolean(cat.isReportedCategory);

  useEffect(() => {
    if (
      !isCurrentCategory ||
      !reported ||
      activeTab !== TAB_DEFAULT ||
      currentCategory?.id !== category.id
    ) {
      return;
    }
    defaultFlowIntakeSnapshotsByCategoryIdRef.current.set(
      category.id,
      readDefaultFlowIntakeSnapshot(cat),
    );
  }, [
    activeTab,
    cat,
    category.id,
    currentCategory?.id,
    isCurrentCategory,
    reported,
  ]);

  useEffect(() => {
    if (!isCurrentCategory) {
      return;
    }
    setActiveTab(cat.isCustomQuestions ? TAB_CUSTOM : TAB_DEFAULT);
  }, [isCurrentCategory, category.id, cat.isCustomQuestions]);

  useEffect(() => {
    if (dynamicQuestionsV1) {
      resetCustomFlowUnavailableTooltipRef.current();
      setCustomFlowUnavailableTooltipRoot(null);
    }
  }, [dynamicQuestionsV1]);

  useEffect(() => {
    if (!dynamicQuestionsV1 && isCurrentCategory && activeTab === TAB_CUSTOM) {
      setActiveTab(TAB_DEFAULT);
      setCurrentCategory((prev) => {
        if (!prev || prev.id !== category.id) {
          return prev;
        }
        const stored = defaultFlowIntakeSnapshotsByCategoryIdRef.current.get(
          prev.id,
        );
        if (stored) {
          return {
            ...mergeCategoryWithDefaultFlowIntakeSnapshot(prev, stored),
            isCustomQuestions: false,
          };
        }
        return {
          ...patchDefaultQuestionFlowVisibleChannels(
            prev,
            vaultTalkEnabled,
            slackEnabled,
            goTogetherEnabled,
          ),
          isCustomQuestions: false,
        };
      });
    }
  }, [
    activeTab,
    category.id,
    dynamicQuestionsV1,
    goTogetherEnabled,
    isCurrentCategory,
    setCurrentCategory,
    slackEnabled,
    vaultTalkEnabled,
  ]);

  useEffect(() => {
    if (!dynamicQuestionsV1) {
      return;
    }
    if (!onDefaultFlowIntakeValidityChange) {
      return;
    }
    if (!isCurrentCategory) {
      onDefaultFlowIntakeValidityChange(true);
      return;
    }
    if (!reported) {
      onDefaultFlowIntakeValidityChange(true);
      return;
    }
    if (activeTab === TAB_CUSTOM) {
      onDefaultFlowIntakeValidityChange(true);
      return;
    }
    onDefaultFlowIntakeValidityChange(isDefaultQuestionFlowIntakeValid(cat));
  }, [
    activeTab,
    cat,
    dynamicQuestionsV1,
    isCurrentCategory,
    onDefaultFlowIntakeValidityChange,
    reported,
  ]);

  const defaultFlowLocked =
    activeTab === TAB_CUSTOM && Boolean(cat.hasActiveLinkedForm);

  const [defaultFlowLockedTooltipRoot, setDefaultFlowLockedTooltipRoot] =
    useState<HTMLElement | null>(null);
  const defaultFlowLockedTooltipVisibility = useTooltipHoverOrFocusVisibility();

  const handleTabChange = createCategoryBodyHandleTabChange({
    isCurrentCategory,
    activeTab,
    dynamicQuestionsV1,
    hasActiveLinkedForm: Boolean(cat.hasActiveLinkedForm),
    categoryId: category.id,
    setActiveTab,
    setCurrentCategory,
    defaultFlowIntakeSnapshotsByCategoryIdRef,
    vaultTalkEnabled,
    slackEnabled,
    goTogetherEnabled,
  });

  const intakeDisabled = !isCurrentCategory || Boolean(cat.hasActiveLinkedForm);

  const renderGoTogetherInfo = () => (
    <>
      <IconTooltipTriggerButton
        type="button"
        ref={setGoTogetherTooltipRoot}
        {...goTogetherTooltipVisibility.hoverHandlers}
        {...goTogetherTooltipVisibility.focusHandlers}
        aria-label={goTogetherDescription}
        data-testid="go-together-info"
      >
        <StyledIcon
          size="20px"
          color={
            theme.isVaultVariant
              ? theme.newPalette.neutral.N800
              : theme.atlas.semantic.color.type.default
          }
          className="uil uil-info-circle"
          aria-hidden
        />
      </IconTooltipTriggerButton>
      {goTogetherTooltipVisibility.visible && goTogetherTooltipRoot && (
        <Tooltip
          text={goTogetherDescription}
          variant="down"
          root={goTogetherTooltipRoot}
          width="280px"
        />
      )}
    </>
  );

  const renderUnavailableTooltip = (
    setRootRef: Dispatch<SetStateAction<HTMLElement | null>>,
    rootRef: HTMLElement | null,
    tooltipVisibility: ReturnType<typeof useTooltipHoverOrFocusVisibility>,
    tooltip: string,
  ) => (
    <>
      <StyledIcon
        size="20px"
        color={
          theme.isVaultVariant
            ? theme.newPalette.neutral.N600
            : theme.atlas.semantic.color.type.muted
        }
        className="uil uil-lock-alt"
        aria-hidden
      />
      <IconTooltipTriggerButton
        type="button"
        ref={setRootRef}
        {...tooltipVisibility.hoverHandlers}
        {...tooltipVisibility.focusHandlers}
        aria-label={tooltip}
        data-testid="unavailable-tooltip"
      >
        <StyledIcon
          size="20px"
          color={
            theme.isVaultVariant
              ? theme.newPalette.neutral.N600
              : theme.atlas.semantic.color.type.muted
          }
          className="uil uil-info-circle"
          aria-hidden
        />
      </IconTooltipTriggerButton>
      {tooltipVisibility.visible && rootRef && (
        <Tooltip text={tooltip} variant="down" root={rootRef} width="280px" />
      )}
    </>
  );

  const renderDefaultIntakeColumn = (invalid: boolean) => (
    <QuestionFlowColumn>
      <SectionHeading>Intake channels</SectionHeading>
      <SectionDescription>
        Select where this category should be available across intake channels.
      </SectionDescription>
      <IntakeChannelRow>
        <IntakeChannelLabel isDisabled={intakeDisabled || !vaultTalkEnabled}>
          VaultTalk
          {!vaultTalkEnabled && (
            <div>
              {renderUnavailableTooltip(
                setCustomFlowVaultTalkTooltipRoot,
                customFlowVaultTalkTooltipRoot,
                vaultTalkTooltipVisibility,
                unavailableTooltip,
              )}
            </div>
          )}
        </IntakeChannelLabel>
        <Switch
          checked={Boolean(cat.displayInVaultTalk)}
          disabled={intakeDisabled || !vaultTalkEnabled}
          onClick={() =>
            toggleIntakeMethod(setCurrentCategory, 'displayInVaultTalk')
          }
          data-testid="display-in-vault-talk"
        />
      </IntakeChannelRow>
      <IntakeChannelRow>
        <IntakeChannelLabel isDisabled={intakeDisabled || !slackEnabled}>
          Slack{' '}
          {!slackEnabled && (
            <div>
              {renderUnavailableTooltip(
                setCustomFlowSlackTooltipRoot,
                customFlowSlackTooltipRoot,
                slackTooltipVisibility,
                unavailableTooltip,
              )}
            </div>
          )}
        </IntakeChannelLabel>
        <Switch
          checked={Boolean(cat.displayInCollaborationToolIntegration)}
          disabled={intakeDisabled || !slackEnabled}
          onClick={() =>
            toggleIntakeMethod(
              setCurrentCategory,
              'displayInCollaborationToolIntegration',
            )
          }
          data-testid="display-in-collaboration-tool-integration"
        />
      </IntakeChannelRow>
      <IntakeChannelRow>
        <IntakeChannelLabel>Vault</IntakeChannelLabel>
        <Switch
          checked={Boolean(cat.displayInApp)}
          disabled={intakeDisabled}
          onClick={() => toggleIntakeMethod(setCurrentCategory, 'displayInApp')}
          data-testid="display-in-app"
        />
      </IntakeChannelRow>
      <IntakeChannelRowSubOption>
        <IntakeChannelLabel>
          GoTogether
          {renderGoTogetherInfo()}
        </IntakeChannelLabel>
        <Switch
          checked={Boolean(cat.allowGoTogether)}
          disabled={intakeDisabled || !goTogetherEnabled}
          onClick={() =>
            toggleIntakeMethod(setCurrentCategory, 'allowGoTogether')
          }
          data-testid="allow-go-together"
        />
      </IntakeChannelRowSubOption>
      {invalid && (
        <ValidationMessage data-testid="default-flow-intake-error">
          Select at least one of VaultTalk, Slack, or Vault.
        </ValidationMessage>
      )}
    </QuestionFlowColumn>
  );

  const renderLegacyIntakeColumn = () => (
    <QuestionFlowColumn>
      <SectionHeading>Intake channels</SectionHeading>
      <SectionDescription>
        Select where this category should be available across intake channels.
      </SectionDescription>
      <IntakeChannelRow>
        <IntakeChannelLabel>Manual Intake</IntakeChannelLabel>
        <Switch
          checked={Boolean(cat.displayInManualIntake)}
          disabled={intakeDisabled}
          onClick={() =>
            toggleIntakeMethod(setCurrentCategory, 'displayInManualIntake')
          }
          data-testid="display-in-manual-intake-toggle"
        />
      </IntakeChannelRow>
      <IntakeChannelRow>
        <IntakeChannelLabel>Open Reporting</IntakeChannelLabel>
        <Switch
          checked={Boolean(cat.displayInOpenReporting)}
          disabled={intakeDisabled}
          onClick={() =>
            toggleIntakeMethod(setCurrentCategory, 'displayInOpenReporting')
          }
          data-testid="display-in-open-reporting-toggle"
        />
      </IntakeChannelRow>
      <IntakeChannelRow>
        <IntakeChannelLabel>Vault app</IntakeChannelLabel>
        <Switch
          checked={Boolean(cat.displayInApp)}
          disabled={intakeDisabled}
          onClick={() => toggleIntakeMethod(setCurrentCategory, 'displayInApp')}
          data-testid="display-in-app-toggle"
        />
      </IntakeChannelRow>
      <IntakeChannelRowSubOption>
        <IntakeChannelLabel isDisabled={intakeDisabled || !goTogetherEnabled}>
          GoTogether
          {renderGoTogetherInfo()}
        </IntakeChannelLabel>
        <Switch
          checked={Boolean(cat.allowGoTogether)}
          disabled={intakeDisabled || !goTogetherEnabled}
          onClick={() =>
            toggleIntakeMethod(setCurrentCategory, 'allowGoTogether')
          }
          data-testid="allow-go-together-toggle"
        />
      </IntakeChannelRowSubOption>
      <IntakeChannelRow>
        <IntakeChannelLabel isDisabled={intakeDisabled || !vaultTalkEnabled}>
          VaultTalk
          {!vaultTalkEnabled && (
            <div>
              {renderUnavailableTooltip(
                setCustomFlowVaultTalkTooltipRoot,
                customFlowVaultTalkTooltipRoot,
                vaultTalkTooltipVisibility,
                unavailableTooltip,
              )}
            </div>
          )}
        </IntakeChannelLabel>
        <Switch
          checked={Boolean(cat.displayInVaultTalk)}
          disabled={intakeDisabled || !vaultTalkEnabled}
          onClick={() =>
            toggleIntakeMethod(setCurrentCategory, 'displayInVaultTalk')
          }
          data-testid="display-in-vault-talk-toggle"
        />
      </IntakeChannelRow>
      <IntakeChannelRow>
        <IntakeChannelLabel isDisabled={intakeDisabled || !slackEnabled}>
          Slack
          {!slackEnabled && (
            <div>
              {renderUnavailableTooltip(
                setCustomFlowSlackTooltipRoot,
                customFlowSlackTooltipRoot,
                slackTooltipVisibility,
                unavailableTooltip,
              )}
            </div>
          )}
        </IntakeChannelLabel>
        <Switch
          checked={Boolean(cat.displayInCollaborationToolIntegration)}
          disabled={intakeDisabled || !slackEnabled}
          onClick={() =>
            toggleIntakeMethod(
              setCurrentCategory,
              'displayInCollaborationToolIntegration',
            )
          }
          data-testid="display-in-collaboration-tool-integration-toggle"
        />
      </IntakeChannelRow>
    </QuestionFlowColumn>
  );

  const renderCustomInfoColumn = () => (
    <QuestionFlowColumn
      data-testid="custom-question-flow-info"
      $backgroundColor={
        theme.isVaultVariant
          ? theme.newPalette.neutral.N100
          : theme.atlas.semantic.color.surface.subtle
      }
    >
      {!cat.hasActiveLinkedForm && (
        <CustomFlowNotice>
          <CustomFlowNoticeHeader>
            <StyledIcon
              size="20px"
              color={
                theme.isVaultVariant
                  ? theme.newPalette.turquoise.T300
                  : theme.atlas.semantic.color.action.primary.default
              }
              className="uil uil-info-circle"
              aria-hidden
            />
            <CustomFlowNoticeTitle>Form required</CustomFlowNoticeTitle>
          </CustomFlowNoticeHeader>
          <CustomFlowNoticeBody>
            Categories with custom questions in Vault require a form before they
            can be set live. You can save your changes, but the category will
            only go live once a form is created.{' '}
            <Link
              to="/form-management"
              size="small"
              data-testid="custom-flow-link-forms"
            >
              Go to Forms to create one.
            </Link>
          </CustomFlowNoticeBody>
        </CustomFlowNotice>
      )}
      <CustomFlowNotice>
        <CustomFlowNoticeHeader>
          <StyledIcon
            size="20px"
            color={
              theme.isVaultVariant
                ? theme.newPalette.yellow.Y400
                : theme.atlas.semantic.color.status.warning.text
            }
            className="uil uil-exclamation-triangle"
            aria-hidden
          />
          <CustomFlowNoticeTitle>
            Only available via Vault
          </CustomFlowNoticeTitle>
        </CustomFlowNoticeHeader>
        <CustomFlowNoticeBody>
          Categories with custom questions can only be submitted via Vault.
        </CustomFlowNoticeBody>
      </CustomFlowNotice>
    </QuestionFlowColumn>
  );

  const renderCustomIntakeColumn = () => (
    <QuestionFlowColumn>
      <SectionHeading>Intake channels</SectionHeading>
      <SectionDescription>
        Select where this category should be available across intake channels.
      </SectionDescription>
      <IntakeChannelRow>
        <IntakeChannelLabel isDisabled={true}>
          VaultTalk
          {renderUnavailableTooltip(
            setCustomFlowVaultTalkTooltipRoot,
            customFlowVaultTalkTooltipRoot,
            vaultTalkTooltipVisibility,
            customIntakeMethodUnavailableTooltip,
          )}
        </IntakeChannelLabel>
        <Switch
          checked={false}
          disabled={true}
          onClick={() => {}}
          data-testid="display-in-vault-talk-custom"
        />
      </IntakeChannelRow>
      <IntakeChannelRow>
        <IntakeChannelLabel isDisabled={true}>
          Slack
          {renderUnavailableTooltip(
            setCustomFlowSlackTooltipRoot,
            customFlowSlackTooltipRoot,
            slackTooltipVisibility,
            customIntakeMethodUnavailableTooltip,
          )}
        </IntakeChannelLabel>
        <Switch
          checked={false}
          disabled={true}
          onClick={() => {}}
          data-testid="display-in-slack-custom"
        />
      </IntakeChannelRow>
      <IntakeChannelRow>
        <IntakeChannelLabel>Vault</IntakeChannelLabel>
        <Switch
          checked={Boolean(cat.displayInApp)}
          disabled={intakeDisabled}
          onClick={() => toggleIntakeMethod(setCurrentCategory, 'displayInApp')}
          data-testid="display-in-app-custom"
        />
      </IntakeChannelRow>
      <IntakeChannelRowSubOption>
        <IntakeChannelLabel>
          GoTogether
          {renderGoTogetherInfo()}
        </IntakeChannelLabel>
        <Switch
          checked={Boolean(cat.allowGoTogether)}
          disabled={intakeDisabled || !goTogetherEnabled}
          onClick={() =>
            toggleIntakeMethod(setCurrentCategory, 'allowGoTogether')
          }
          data-testid="allow-go-together-custom"
        />
      </IntakeChannelRowSubOption>
    </QuestionFlowColumn>
  );

  const defaultIntakeInvalid =
    dynamicQuestionsV1 &&
    reported &&
    activeTab === TAB_DEFAULT &&
    isCurrentCategory &&
    !isDefaultQuestionFlowIntakeValid(cat);
  const reportTypeName = `category-report-type-${category.id}`;
  const customQuestionFlowLocked =
    isLegacyMode ||
    !dynamicQuestionsV1 ||
    (activeTab === TAB_DEFAULT && Boolean(cat.hasActiveLinkedForm));

  return (
    <DynamicLayoutRoot data-testid="category-body-dynamic-questions">
      <TopCategoryRow data-testid="determined-category">
        <TopCategorySwitchCell>
          <Switch
            checked={Boolean(cat.isDeterminedCategory)}
            disabled={!isCurrentCategory}
            onClick={() =>
              toggleCategoryProperty(setCurrentCategory, 'isDeterminedCategory')
            }
            data-testid="determined-category-toggle"
          />
        </TopCategorySwitchCell>
        <TopCategoryTitle>Determined category</TopCategoryTitle>
        <TopCategoryDescription>
          Allow case managers to select this category in case investigations as
          determined category.
        </TopCategoryDescription>
      </TopCategoryRow>
      <TopCategoryRow data-testid="reported-category">
        <TopCategorySwitchCell>
          <Switch
            checked={Boolean(cat.isReportedCategory)}
            disabled={!isCurrentCategory}
            onClick={() =>
              toggleReportedCategoryOrQuestion(
                setCurrentCategory,
                vaultTalkEnabled,
                oprEnabled,
                goTogetherEnabled,
                slackEnabled,
              )
            }
            data-testid="reported-category-toggle"
          />
        </TopCategorySwitchCell>
        <TopCategoryTitle>Reported category</TopCategoryTitle>
        <TopCategoryDescription>
          Allow reporters to select this category across the selected intake
          channel(s).
        </TopCategoryDescription>
      </TopCategoryRow>

      {reported && (
        <QuestionFlowOuter data-testid="question-flow-section">
          {isCurrentCategory ? (
            <>
              <TabRow role="tablist">
                <TabButton
                  as={defaultFlowLocked ? 'div' : 'button'}
                  {...(!defaultFlowLocked ? { type: 'button' as const } : {})}
                  role="tab"
                  aria-selected={activeTab === TAB_DEFAULT}
                  aria-disabled={defaultFlowLocked}
                  $active={activeTab === TAB_DEFAULT}
                  $segment="first"
                  $locked={defaultFlowLocked}
                  onClick={() => handleTabChange(TAB_DEFAULT)}
                  data-testid="tab-default-question-flow"
                >
                  {defaultFlowLocked ? (
                    <TabButtonLabelRow>
                      <TabButtonLabelText>
                        Default question flow
                      </TabButtonLabelText>
                      <TabButtonTrailing>
                        <StyledIcon
                          size="20px"
                          color={
                            theme.isVaultVariant
                              ? theme.newPalette.neutral.N600
                              : theme.atlas.semantic.color.type.muted
                          }
                          className="uil uil-lock"
                          aria-hidden
                          data-testid="tab-default-question-flow-lock"
                        />
                        <IconTooltipTriggerButton
                          type="button"
                          ref={setDefaultFlowLockedTooltipRoot}
                          {...defaultFlowLockedTooltipVisibility.hoverHandlers}
                          {...defaultFlowLockedTooltipVisibility.focusHandlers}
                          aria-label={flowBlockedByLinkedFormTooltip}
                          data-testid="tab-default-question-flow-blocked-info"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <StyledIcon
                            size="20px"
                            color={
                              theme.isVaultVariant
                                ? theme.newPalette.neutral.N600
                                : theme.atlas.semantic.color.type.muted
                            }
                            className="uil uil-info-circle"
                            aria-hidden
                          />
                        </IconTooltipTriggerButton>
                      </TabButtonTrailing>
                    </TabButtonLabelRow>
                  ) : (
                    'Default question flow'
                  )}
                </TabButton>
                <TabButton
                  as={customQuestionFlowLocked ? 'div' : 'button'}
                  {...(!customQuestionFlowLocked
                    ? { type: 'button' as const }
                    : {})}
                  role="tab"
                  aria-selected={activeTab === TAB_CUSTOM}
                  aria-disabled={customQuestionFlowLocked}
                  tabIndex={0}
                  $active={activeTab === TAB_CUSTOM}
                  $segment="last"
                  $locked={customQuestionFlowLocked}
                  onClick={() =>
                    !customQuestionFlowLocked && handleTabChange(TAB_CUSTOM)
                  }
                  data-testid="tab-custom-question-flow"
                  {...(customQuestionFlowLocked
                    ? {
                        ...customFlowUnavailableTooltipVisibility.hoverHandlers,
                      }
                    : {})}
                >
                  {customQuestionFlowLocked ? (
                    <TabButtonLabelRow>
                      <TabButtonLabelText>
                        Custom question flow
                      </TabButtonLabelText>
                      <TabButtonTrailing>
                        <StyledIcon
                          size="20px"
                          color={
                            theme.isVaultVariant
                              ? theme.newPalette.neutral.N600
                              : theme.atlas.semantic.color.type.muted
                          }
                          className="uil uil-lock"
                          aria-hidden
                          data-testid="tab-custom-question-flow-lock"
                        />
                        <IconTooltipTriggerButton
                          ref={setCustomFlowUnavailableTooltipRoot}
                          {...customFlowUnavailableTooltipVisibility.hoverHandlers}
                          {...customFlowUnavailableTooltipVisibility.focusHandlers}
                          aria-label={unavailableTooltip}
                          data-testid="tab-custom-question-flow-unavailable-info"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <StyledIcon
                            size="20px"
                            color={
                              theme.isVaultVariant
                                ? theme.newPalette.neutral.N800
                                : theme.atlas.semantic.color.type.default
                            }
                            className="uil uil-info-circle"
                            aria-hidden
                          />
                        </IconTooltipTriggerButton>
                      </TabButtonTrailing>
                    </TabButtonLabelRow>
                  ) : (
                    'Custom question flow'
                  )}
                </TabButton>
              </TabRow>
              {customQuestionFlowLocked &&
                customFlowUnavailableTooltipVisibility.visible &&
                customFlowUnavailableTooltipRoot && (
                  <Tooltip
                    text={
                      dynamicQuestionsV1
                        ? flowBlockedByLinkedFormTooltip
                        : unavailableTooltip
                    }
                    variant="down"
                    root={customFlowUnavailableTooltipRoot}
                    width="280px"
                  />
                )}
              {defaultFlowLocked &&
                defaultFlowLockedTooltipVisibility.visible &&
                defaultFlowLockedTooltipRoot && (
                  <Tooltip
                    text={flowBlockedByLinkedFormTooltip}
                    variant="down"
                    root={defaultFlowLockedTooltipRoot}
                    width="280px"
                  />
                )}
            </>
          ) : null}

          {(!isCurrentCategory ||
            activeTab === TAB_DEFAULT ||
            !dynamicQuestionsV1) && (
            <QuestionFlowContent>
              <QuestionFlowColumn>
                <SectionHeading>Report type</SectionHeading>
                <SectionDescription>
                  Select Misconduct report for code of conduct issues that
                  follow the full reporting process or Inquiry for
                  non-code-of-conduct topics.
                </SectionDescription>
                <RadioStack>
                  <RadioButton
                    id={`${reportTypeName}-misconduct`}
                    name={reportTypeName}
                    label="Misconduct report"
                    labelPosition="right"
                    checked={!cat.question}
                    disabled={
                      intakeDisabled || Boolean(cat.hasActiveLinkedForm)
                    }
                    onClick={() =>
                      setCategoryQuestion(setCurrentCategory, false)
                    }
                    onChange={() =>
                      setCategoryQuestion(setCurrentCategory, false)
                    }
                    data-testid="report-type-misconduct"
                  />
                  <RadioButton
                    id={`${reportTypeName}-inquiry`}
                    name={reportTypeName}
                    label="Inquiry"
                    labelPosition="right"
                    checked={cat.question}
                    disabled={
                      intakeDisabled || Boolean(cat.hasActiveLinkedForm)
                    }
                    onClick={() =>
                      setCategoryQuestion(setCurrentCategory, true)
                    }
                    onChange={() =>
                      setCategoryQuestion(setCurrentCategory, true)
                    }
                    data-testid="report-type-inquiry"
                  />
                </RadioStack>
              </QuestionFlowColumn>
              {isLegacyMode
                ? renderLegacyIntakeColumn()
                : renderDefaultIntakeColumn(defaultIntakeInvalid)}
            </QuestionFlowContent>
          )}

          {isCurrentCategory &&
          activeTab === TAB_CUSTOM &&
          dynamicQuestionsV1 ? (
            <QuestionFlowContent>
              {renderCustomInfoColumn()}
              {renderCustomIntakeColumn()}
            </QuestionFlowContent>
          ) : null}
        </QuestionFlowOuter>
      )}
    </DynamicLayoutRoot>
  );
};
